/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    displayAsPageBanner = "displayAsPageBanner",
    overrideTitle = "overrideTitle",
    backgroundImage = "backgroundImage",
    iconImage = "iconImage",
    secondaryText = "secondaryText",
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> & {
        fields: {
            [fields.displayAsPageBanner]: boolean;
            [fields.overrideTitle]: string;
            [fields.backgroundImage]: string;
            [fields.iconImage]: string;
            [fields.secondaryText]: string;
        };
    };

export interface PageTitleStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    iconImage?: InjectableCss;
    title?: InjectableCss;
    secondaryText?: InjectableCss;
}

export const pageTitleStyles: PageTitleStyles = {
    wrapper: {
        css: css`
            width: 100%;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 200px;
            background-size: cover;
            background-position: center;
        `,
    },
    contentWrapper: {
        css: css`
            max-width: 1140px;
            margin: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 40px 20px;
            position: relative;
            z-index: 0;
            margin-left: 0;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-right: 14rem;
                    `,
                    css`
                        margin-right: 15rem;
                    `,
                    css`
                        margin-right: 15rem;
                    `,
                ])}
        `,
    },
    iconImage: {
        css: css`
            margin-bottom: 20px;
            max-height: 80px;
            width: auto;
        `,
    },
    title: {
        css: css`
            margin-bottom: 20px;
            font-size: 32px;
            font-weight: 700;
            line-height: 2rem;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        font-size: 48px;
                        line-height: 4rem;
                    `,
                    css`
                        font-size: 48px;
                        line-height: 4rem;
                    `,
                    css`
                        font-size: 60px;
                        line-height: 4rem;
                    `,
                    css`
                        font-size: 60px;
                        line-height: 4rem;
                    `,
                ])}
        `,
    },
    secondaryText: {
        css: css`
            margin-top: 10px;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        margin-left: 8rem;
                        margin-right: 8rem;
                    `,
                    css`
                        margin-right: 15rem;
                        margin-left: 15rem;
                    `,
                ])}
        `,
    },
};

const PageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    if (!fields.displayAsPageBanner) {
        return (
            <Typography variant="h2" as="h1" data-test-selector="PageTitle">
                {fields.overrideTitle || pageTitle}
            </Typography>
        );
    }

    const backgroundImageUrl = fields.backgroundImage ? parseImageUrl(fields.backgroundImage).url : "";

    const wrapperStyles = {
        css: css`
            ${pageTitleStyles.wrapper?.css || ""}
            ${backgroundImageUrl ? `background-image: url(${backgroundImageUrl});` : ""}
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <StyledWrapper {...pageTitleStyles.contentWrapper}>
                {fields.iconImage && (
                    <Img src={fields.iconImage} loading="lazy" alt="Icon" {...pageTitleStyles.iconImage} />
                )}
                <Typography variant="h2" as="h1" data-test-selector="PageTitle" {...pageTitleStyles.title}>
                    {fields.overrideTitle || pageTitle}
                </Typography>
                {fields.secondaryText && (
                    <Typography {...pageTitleStyles.secondaryText}>
                        {parse(fields.secondaryText, parserOptions)}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.displayAsPageBanner,
                editorTemplate: "CheckboxField",
                displayName: "Display as Page Banner",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                displayName: "Override Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                displayName: "Background Image",
                defaultValue: "",
                isVisible: item => item?.fields[fields.displayAsPageBanner],
            },
            {
                fieldType: "General",
                name: fields.iconImage,
                editorTemplate: "ImagePickerField",
                displayName: "Icon Image",
                defaultValue: "",
                isVisible: item => item?.fields[fields.displayAsPageBanner],
            },
            {
                fieldType: "Contextual",
                name: fields.secondaryText,
                editorTemplate: "RichTextField",
                displayName: "Secondary Text",
                defaultValue: "",
                extendedConfig: { height: 100 },
                isVisible: item => item?.fields[fields.displayAsPageBanner],
            },
        ],
    },
};

export default widgetModule;
